import {
  StudioState,
  VideoModel,
  VideoCreatorErrorCode,
} from '../../types/store';
import {
  updateVideoCreatorPrompt,
  updateVideoCreatorModel,
  updateVideoCreatorAspectRatio,
  updateVideoCreatorDuration,
  updateVideoCreatorSound,
  updateVideoCreatorStatus,
  updateVideoCreatorReferenceDeviation,
  updateVideoCreatorErrorCode,
  initializeVideoCreator,
  updateVideoCreatorRestrictions,
  updateVideoCreatorPricePoints,
  updateVideoCreatorDidCreate,
  updateVideoCreatorIsCalculatingCost,
  updateVideoCreatorPendingGenerationId,
} from '../../actions/studio/videoCreator';

const defaultVideoCreatorState = {
  prompt: '',
  model: VideoModel.HAILUO,
  aspectRatio: '4:3' as const,
  duration: 6,
  sound: false,
  status: 'idle' as const,
  errorCode: VideoCreatorErrorCode.NO_ERROR,
  restrictions: undefined,
  pointsPackages: [],
  pricePoints: 0,
  didCreate: false,
  isCalculatingCost: false,
  pendingGenerationId: undefined,
};

export const handleUpdateVideoCreatorPrompt = (
  state: StudioState,
  action: ReturnType<typeof updateVideoCreatorPrompt>
): StudioState => ({
  ...state,
  videoCreator: {
    ...state.videoCreator,
    prompt: action.payload.prompt,
  },
});

export const handleUpdateVideoCreatorModel = (
  state: StudioState,
  action: ReturnType<typeof updateVideoCreatorModel>
): StudioState => ({
  ...state,
  videoCreator: {
    ...state.videoCreator,
    model: action.payload.model,
  },
});

export const handleUpdateVideoCreatorAspectRatio = (
  state: StudioState,
  action: ReturnType<typeof updateVideoCreatorAspectRatio>
): StudioState => ({
  ...state,
  videoCreator: {
    ...state.videoCreator,
    aspectRatio: action.payload.aspectRatio,
  },
});

export const handleUpdateVideoCreatorDuration = (
  state: StudioState,
  action: ReturnType<typeof updateVideoCreatorDuration>
): StudioState => ({
  ...state,
  videoCreator: {
    ...state.videoCreator,
    duration: action.payload.duration,
  },
});

export const handleUpdateVideoCreatorSound = (
  state: StudioState,
  action: ReturnType<typeof updateVideoCreatorSound>
): StudioState => ({
  ...state,
  videoCreator: {
    ...state.videoCreator,
    sound: action.payload.sound,
  },
});

export const handleUpdateVideoCreatorStatus = (
  state: StudioState,
  action: ReturnType<typeof updateVideoCreatorStatus>
): StudioState => ({
  ...state,
  videoCreator: {
    ...state.videoCreator,
    status: action.payload.status,
  },
});

export const handleUpdateVideoCreatorReferenceDeviation = (
  state: StudioState,
  action: ReturnType<typeof updateVideoCreatorReferenceDeviation>
): StudioState => ({
  ...state,
  videoCreator: {
    ...state.videoCreator,
    referenceDeviation: action.payload.referenceDeviation,
  },
});

export const handleUpdateVideoCreatorErrorCode = (
  state: StudioState,
  action: ReturnType<typeof updateVideoCreatorErrorCode>
): StudioState => ({
  ...state,
  videoCreator: {
    ...state.videoCreator,
    errorCode: action.payload.errorCode,
  },
});

export const handleResetVideoCreator = (state: StudioState): StudioState => ({
  ...state,
  videoCreator: defaultVideoCreatorState,
});

export const handleInitializeVideoCreator = (
  state: StudioState,
  action: ReturnType<typeof initializeVideoCreator>
): StudioState => {
  const { restrictions } = action.payload.initData;
  // pointsPackages is stubbed in server route until API returns it
  const pointsPackages = (action.payload.initData as any).pointsPackages;
  return {
    ...state,
    videoCreator: {
      ...state.videoCreator,
      restrictions,
      pointsPackages: pointsPackages
        ? pointsPackages.map((pointsPackage: any) => ({
            points: pointsPackage.pricePoints,
            price: pointsPackage.priceDollars,
            subproductId: pointsPackage.subproductId,
          }))
        : [],
    },
  };
};

export const handleUpdateVideoCreatorRestrictions = (
  state: StudioState,
  action: ReturnType<typeof updateVideoCreatorRestrictions>
): StudioState => ({
  ...state,
  videoCreator: {
    ...state.videoCreator,
    restrictions: action.payload.restrictions,
  },
});

export const handleUpdateVideoCreatorPricePoints = (
  state: StudioState,
  action: ReturnType<typeof updateVideoCreatorPricePoints>
): StudioState => ({
  ...state,
  videoCreator: {
    ...state.videoCreator,
    pricePoints: action.payload.pricePoints,
  },
});

export const handleUpdateVideoCreatorDidCreate = (
  state: StudioState,
  action: ReturnType<typeof updateVideoCreatorDidCreate>
): StudioState => ({
  ...state,
  videoCreator: {
    ...state.videoCreator,
    didCreate: action.payload.didCreate,
  },
});

export const handleUpdateVideoCreatorIsCalculatingCost = (
  state: StudioState,
  action: ReturnType<typeof updateVideoCreatorIsCalculatingCost>
): StudioState => ({
  ...state,
  videoCreator: {
    ...state.videoCreator,
    isCalculatingCost: action.payload.isCalculatingCost,
  },
});

export const handleUpdateVideoCreatorPendingGenerationId = (
  state: StudioState,
  action: ReturnType<typeof updateVideoCreatorPendingGenerationId>
): StudioState => ({
  ...state,
  videoCreator: {
    ...state.videoCreator,
    pendingGenerationId: action.payload.generationId,
  },
});
